import { useCallback, useEffect, useState } from "react";
import { get as idbGet, set as idbSet, del as idbDel } from "idb-keyval";
import moment from "moment";
import { useAppDispatch } from "../store/hooks";
import useCurrentResourceCentre from "./useCurrentResourceCentre";
import { notificationAdd as addNotification } from "../actions/notification";

export interface SavePhotoOptions {
  clientName: string;
  clientId?: number;
}

export interface UseLocalPhotoSaveReturn {
  isSupported: boolean;
  folderName: string | null;
  selectFolder: () => Promise<string | null>;
  clearFolder: () => Promise<void>;
  savePhoto: (blob: Blob, options: SavePhotoOptions) => Promise<SaveResult>;
}

export type SaveResult =
  | { status: "saved"; filename: string; path: string }
  | { status: "skipped"; reason: "no_folder" | "unsupported" }
  | { status: "permission_denied" }
  | { status: "error"; error: unknown };

const IDB_KEY = "okhati_patient_photo_folder_handle";

const isFileSystemAccessSupported = (): boolean =>
  typeof window !== "undefined" &&
  "showDirectoryPicker" in window &&
  typeof (window as Window).showDirectoryPicker === "function";

const buildFilename = (options: SavePhotoOptions): string => {
  const timestamp = moment().format("YYYY-MMM-DD_HH-mm-ss-SSS");

  return `${options.clientId || ""}_${options.clientName}_${timestamp}.jpg`;
};

const verifyPermission = async (
  handle: FileSystemDirectoryHandle,
  mode: FileSystemPermissionMode = "readwrite"
): Promise<boolean> => {
  const current = await handle.queryPermission({ mode });
  if (current === "granted") return true;

  const requested = await handle.requestPermission({ mode });
  return requested === "granted";
};

const useLocalPhotoSave = (): UseLocalPhotoSaveReturn => {
  const isSupported = isFileSystemAccessSupported();
  const dispatch = useAppDispatch();
  const { settings } = useCurrentResourceCentre();

  const [dirHandle, setDirHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [folderName, setFolderName] = useState<string | null>(null);

  useEffect(() => {
    if (!isSupported) return;

    (async () => {
      try {
        const stored = await idbGet<FileSystemDirectoryHandle>(IDB_KEY);
        if (stored) {
          setDirHandle(stored);
          setFolderName(stored.name);
        }
      } catch (e) {
        console.log(e);
      }
    })();
  }, [isSupported]);

  const selectFolder = useCallback(async (): Promise<string | null> => {
    if (!isSupported) return null;

    try {
      const handle = await (window as Window & typeof globalThis).showDirectoryPicker({
        id: "okhati-patient-photos",
        mode: "readwrite",
        startIn: "pictures"
      });
      await idbSet(IDB_KEY, handle);
      setDirHandle(handle);
      setFolderName(handle.name);
      return handle.name;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") return null;
      throw err;
    }
  }, [isSupported]);

  const clearFolder = useCallback(async (): Promise<void> => {
    await idbDel(IDB_KEY);
    setDirHandle(null);
    setFolderName(null);
  }, []);

  const savePhoto = useCallback(
    async (blob: Blob, options: SavePhotoOptions): Promise<SaveResult> => {
      if (settings?.clientSettings.saveClientPhotoLocally && !dirHandle) {
        dispatch(
          addNotification({
            id: new Date().getUTCMilliseconds(),
            variant: "info",
            message: "Could not save Patient photo on PC, Please select folder from settings",
            autoTimeout: true
          })
        );
      }
      if (!isSupported) return { status: "skipped", reason: "unsupported" };
      if (!dirHandle) return { status: "skipped", reason: "no_folder" };

      try {
        const hasPermission = await verifyPermission(dirHandle);
        if (!hasPermission) return { status: "permission_denied" };

        const filename = buildFilename(options);
        const fileHandle = await dirHandle.getFileHandle(filename, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();

        return {
          status: "saved",
          filename,
          path: `${dirHandle.name}/${filename}`
        };
      } catch (error) {
        return { status: "error", error };
      }
    },
    [isSupported, dirHandle]
  );

  return { isSupported, folderName, selectFolder, clearFolder, savePhoto };
};

export default useLocalPhotoSave;
